/**
 * Plate-math calculator — given a target loaded weight, the bar weight, and a
 * list of available plate denominations, returns the plates to load on EACH
 * side of the bar (the bar itself is shared, plates are mirrored).
 *
 * Greedy by largest plate first. If the remaining weight isn't divisible by
 * 2 × smallest plate, we return what we can plus a `remainder` so the UI can
 * surface "can't quite hit 102.6 — closest is 102.5".
 *
 * Defaults match standard commercial gym inventories:
 *   kg: 20kg bar + plates 25, 20, 15, 10, 5, 2.5, 1.25
 *   lb: 45lb bar + plates 45, 35, 25, 10, 5, 2.5
 */

export const DEFAULT_INVENTORY_KG = {
    barWeight: 20,
    plates: [25, 20, 15, 10, 5, 2.5, 1.25],
};

export const DEFAULT_INVENTORY_LB = {
    barWeight: 45,
    plates: [45, 35, 25, 10, 5, 2.5],
};

export function inventoryForUnits(units) {
    return units === 'lb' ? DEFAULT_INVENTORY_LB : DEFAULT_INVENTORY_KG;
}

/**
 * @param {number} target  Total loaded weight (bar + plates).
 * @param {number} barWeight  Bar weight (e.g. 20kg).
 * @param {number[]} plates  Available plate denominations (desc-sortable).
 * @returns {{perSide: {plate:number,count:number}[], totalActual:number, remainder:number, overBar:boolean}}
 */
export function calculatePlates(target, barWeight, plates) {
    const t = Number(target) || 0;
    const bar = Number(barWeight) || 0;
    if (t <= 0) {
        return { perSide: [], totalActual: 0, remainder: 0, overBar: false };
    }
    if (t < bar) {
        // Target below empty-bar weight — can't be loaded.
        return {
            perSide: [],
            totalActual: bar,
            remainder: t - bar,
            overBar: false,
        };
    }
    const sorted = [...plates].sort((a, b) => b - a);
    let perSide = t === bar ? 0 : (t - bar) / 2;
    const out = [];
    for (const p of sorted) {
        if (perSide <= 0) break;
        // Use Math.floor + tiny epsilon to avoid float comparison issues.
        const count = Math.floor((perSide + 1e-9) / p);
        if (count > 0) {
            out.push({ plate: p, count });
            perSide -= count * p;
        }
    }
    const remainder = round1(perSide * 2); // total un-loaded weight
    const loadedPerSide = (t - bar) / 2 - perSide;
    const totalActual = round1(bar + loadedPerSide * 2);
    return {
        perSide: out,
        totalActual,
        remainder,
        overBar: t > bar,
    };
}

function round1(n) {
    return Math.round(n * 100) / 100;
}
